import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

import {
  Yyww,
  YywwResponse,
  YywwListFilters,
} from '../models/yyww.model';

@Injectable({ providedIn: 'root' })
export class YywwService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly endpoint = `${this.baseUrl}/api/yywws`;

  constructor(private http: HttpClient) {}

  getYywws(
    pageNumber: number = 1,
    pageSize: number = 20,
    filters?: YywwListFilters,
  ): Observable<YywwResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http.get<YywwResponse>(this.endpoint, { params }).pipe(
      catchError((err) => {
        console.error('Error fetching yywws:', err);
        return of(null);
      }),
    );
  }

  getAllYywws(filters?: YywwListFilters): Observable<Yyww[]> {
    return this.fetchAllPages<YywwResponse, Yyww>(this.endpoint, filters);
  }

  createYear(year: number): Observable<boolean> {
    return this.http
      .post(`${this.endpoint}/year/${year}`, {}, { responseType: 'text' })
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error(`Error creating yywws for year ${year}:`, err);
          return of(false);
        }),
      );
  }

  deleteYear(year: number): Observable<boolean> {
    return this.http
      .delete(`${this.endpoint}/year/${year}`, { responseType: 'text' })
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error(`Error deleting yywws for year ${year}:`, err);
          return of(false);
        }),
      );
  }

  private fetchAllPages<
    T extends { currentPage: number; totalPages: number; data: K[] },
    K,
  >(url: string, filters?: YywwListFilters): Observable<K[]> {
    const pageSize = 200;

    const buildParams = (pageNumber: number): HttpParams => {
      let params = new HttpParams()
        .set('pageNumber', String(pageNumber))
        .set('pageSize', String(pageSize));

      const searchTerm = String(filters?.searchTerm ?? '').trim();
      if (searchTerm) {
        params = params.set('searchTerm', searchTerm);
      }

      return params;
    };

    return this.http
      .get<T>(url, { params: buildParams(1) })
      .pipe(
        expand((res) =>
          res.currentPage < res.totalPages
            ? this.http.get<T>(url, {
                params: buildParams(res.currentPage + 1),
              })
            : of(),
        ),
        map((res) => res.data),
        reduce((all, page) => [...all, ...page], [] as K[]),
        catchError((err) => {
          console.error('Error fetching paginated yywws:', err);
          return of([]);
        }),
      );
  }
}